/**
 * WebOs Kernel - Notification Service
 *
 * Service intégré au kernel pour afficher les notifications système.
 * Les apps utilisent l'IPC (REQ_NOTIFY_SEND) pour envoyer des notifications.
 * Ce service gère le rendu DOM des notifications dans le main thread.
 */

import { createLogger } from '../utils/debug.js';

const log = createLogger('NotificationService');

// Styles CSS pour les notifications
const NOTIFICATION_STYLES = `
.webos-notifications {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 20000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

.webos-notification {
  background: #1e1e2e;
  border-left: 4px solid #89b4fa;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  padding: 15px 20px;
  min-width: 300px;
  max-width: 400px;
  animation: webos-notif-slideIn 0.3s ease-out;
  pointer-events: auto;
  cursor: pointer;
  transition: opacity 0.3s, transform 0.3s;
}

.webos-notification:hover {
  transform: translateX(-5px);
}

.webos-notification.info { border-color: #89b4fa; }
.webos-notification.success { border-color: #a6e3a1; }
.webos-notification.warning { border-color: #f9e2af; }
.webos-notification.error { border-color: #f38ba8; }

.webos-notification.closing {
  animation: webos-notif-slideOut 0.3s ease-in forwards;
}

@keyframes webos-notif-slideIn {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes webos-notif-slideOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100px);
  }
}

.webos-notification-title {
  color: #cdd6f4;
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
}

.webos-notification-message {
  color: #a6adc8;
  font-family: 'Segoe UI', sans-serif;
  font-size: 13px;
  line-height: 1.4;
}

.webos-notification-time {
  color: #6c7086;
  font-size: 11px;
  margin-top: 8px;
}
`;

/**
 * Service de notifications intégré au kernel
 */
export class NotificationService {
  constructor() {
    /** @type {HTMLDivElement|null} */
    this.container = null;

    /** @type {HTMLStyleElement|null} */
    this.styleElement = null;

    /** @type {Array<{id: number, element: HTMLDivElement, timeout: number|null}>} */
    this.notifications = [];

    /** @type {number} */
    this.notificationId = 0;

    /** @type {boolean} */
    this.enabled = false;

    /** @type {number} */
    this.totalSent = 0;
  }

  /**
   * Démarre le service de notifications
   * Appelé au boot du kernel après que le DOM est prêt
   */
  start() {
    if (this.enabled) {
      log.debug('Notification service already running');
      return;
    }

    // Injecter les styles
    this.injectStyles();

    // Créer le container des notifications
    this.container = document.createElement('div');
    this.container.className = 'webos-notifications';
    this.container.id = 'webos-notifications';
    document.body.appendChild(this.container);

    this.enabled = true;
    log.info('Notification service started');
  }

  /**
   * Arrête le service de notifications
   */
  stop() {
    if (!this.enabled) {
      log.debug('Notification service not running');
      return;
    }

    // Fermer toutes les notifications
    this.clearAll();

    // Retirer le container
    if (this.container) {
      this.container.remove();
      this.container = null;
    }

    // Retirer les styles
    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }

    this.enabled = false;
    log.info('Notification service stopped');
  }

  /**
   * Injecte les styles CSS
   */
  injectStyles() {
    if (document.getElementById('webos-notification-styles')) {
      return;
    }

    this.styleElement = document.createElement('style');
    this.styleElement.id = 'webos-notification-styles';
    this.styleElement.textContent = NOTIFICATION_STYLES;
    document.head.appendChild(this.styleElement);
  }

  /**
   * Affiche une notification
   * @param {string} title - Titre de la notification
   * @param {string} message - Message de la notification
   * @param {string} type - Type: 'info' | 'success' | 'warning' | 'error'
   * @param {number} duration - Durée en ms (0 = persistant)
   * @returns {number} ID de la notification
   */
  send(title, message, type = 'info', duration = 5000) {
    if (!this.enabled || !this.container) {
      log.warn('Notification service not enabled, cannot send notification');
      return -1;
    }

    const id = ++this.notificationId;
    const now = new Date();
    const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    // Créer l'élément
    const notification = document.createElement('div');
    notification.className = `webos-notification ${type}`;
    notification.dataset.id = String(id);

    notification.innerHTML = `
      <div class="webos-notification-title">${this.escapeHtml(title)}</div>
      <div class="webos-notification-message">${this.escapeHtml(message)}</div>
      <div class="webos-notification-time">${timeStr}</div>
    `;

    // Clic pour fermer
    notification.onclick = () => this.dismiss(id);

    // Ajouter au container
    this.container.appendChild(notification);

    // Timer pour auto-dismiss
    let timeout = null;
    if (duration > 0) {
      timeout = setTimeout(() => this.dismiss(id), duration);
    }

    this.notifications.push({ id, element: notification, timeout });
    this.totalSent++;

    log.debug(`Notification sent: [${type}] ${title}`);

    return id;
  }

  /**
   * Ferme une notification par son ID
   * @param {number} id
   */
  dismiss(id) {
    const index = this.notifications.findIndex(n => n.id === id);
    if (index === -1) return;

    const { element, timeout } = this.notifications[index];

    // Annuler le timeout si présent
    if (timeout) {
      clearTimeout(timeout);
    }

    // Animation de fermeture
    element.classList.add('closing');

    setTimeout(() => {
      element.remove();
      this.notifications.splice(index, 1);
    }, 300);
  }

  /**
   * Ferme toutes les notifications
   */
  clearAll() {
    for (const { element, timeout } of this.notifications) {
      if (timeout) {
        clearTimeout(timeout);
      }
      element.remove();
    }
    this.notifications = [];
    log.debug('All notifications cleared');
  }

  /**
   * Retourne le statut du service
   * @returns {Object}
   */
  getStatus() {
    return {
      enabled: this.enabled,
      activeCount: this.notifications.length,
      totalSent: this.totalSent
    };
  }

  /**
   * Échappe le HTML pour éviter XSS
   * @param {string} text
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Instance singleton
let instance = null;

/**
 * Obtient l'instance du NotificationService
 * @returns {NotificationService}
 */
export function getNotificationService() {
  if (!instance) {
    instance = new NotificationService();
  }
  return instance;
}
